import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { GameMode, GameState } from './types';
import { getLevelById, LevelGoalType, getLevelName, getLevelDescription } from './levelConfig';
import GameRulesButton from './GameRulesButton';
import LanguageSwitcher from './LanguageSwitcher';
import AuthButton from './AuthButton';

// 游戏状态面板样式
const StatusPanelContainer = styled.div<{ $isCollapsed?: boolean }>`
  background-color: white;
  border-radius: 12px;
  padding: 15px;
  margin: 6px 0;
  width: 100%;
  max-width: 370px;
  box-shadow: ${props => props.$isCollapsed 
    ? '0 2px 6px rgba(0, 0, 0, 0.08)' 
    : '0 4px 8px rgba(0, 0, 0, 0.1)'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  height: ${props => props.$isCollapsed ? 'auto' : 'auto'};
  transform: translateY(0);
  position: relative;
`;
// 自定义游戏规则按钮样式
const StyledGameRulesButton = styled(GameRulesButton)``;

const StatusPanelHeader = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  // gap: 12px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.$isCollapsed ? '0' : '10px'};
  border-bottom: ${props => props.$isCollapsed ? 'none' : '1px solid #eee'};
  padding-bottom: ${props => props.$isCollapsed ? '0' : '10px'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StatusGrid = styled.div<{ $isCollapsed?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
  max-height: ${props => props.$isCollapsed ? '60px' : '500px'};
  opacity: 1;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  border-radius: ${props => props.$isCollapsed ? '8px' : '0'};
  background-color: ${props => props.$isCollapsed ? '#fafafa' : 'transparent'};
  padding: ${props => props.$isCollapsed ? '8px' : '0'};
  box-shadow: ${props => props.$isCollapsed ? '0 1px 3px rgba(0, 0, 0, 0.03) inset' : 'none'};
`;

const StatusItem = styled.div<{ $isVisible?: boolean; $index?: number }>`
  display: ${props => props.$isVisible !== false ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  opacity: ${props => props.$isVisible !== false ? 1 : 0};
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
              transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              color 0.3s ease;
  transition-delay: ${props => props.$index !== undefined ? `${props.$index * 0.05}s` : '0s'};
  transform: translateY(${props => props.$isVisible !== false ? '0' : '10px'});
`;

const StatusLabel = styled.div<{ $isImportant?: boolean }>`
  font-size: 14px;
  color: ${props => props.$isImportant ? '#333' : '#333'};
  font-weight: ${props => props.$isImportant ? '500' : 'normal'};
`;

const StatusValue = styled.div<{ $isImportant?: boolean }>`
  font-size: ${props => props.$isImportant ? '22px' : '20px'};
  color: ${props => props.$isImportant ? '#333' : '#333'};
  transition: all 0.3s ease;
`;

// 添加淡入动画
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 水平展开动画
const expandRight = keyframes`
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 60px;
    opacity: 1;
  }
`;

// 闪光效果
const shimmer = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// 闪烁高亮
const pulse = keyframes`
  0% {
    box-shadow: 0 2px 5px rgba(78, 205, 196, 0.1);
  }
  50% {
    box-shadow: 0 2px 15px rgba(78, 205, 196, 0.4);
  }
  100% {
    box-shadow: 0 2px 5px rgba(78, 205, 196, 0.1);
  }
`;

const LevelInfoContainer = styled.div<{ $isVisible?: boolean }>`
  margin-top: 10px;
  max-height: ${props => props.$isVisible ? '200px' : '0'};
  opacity: ${props => props.$isVisible ? '1' : '0'};
  overflow: hidden;
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), 
              opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top center;
`;

const LevelInfo = styled.div`
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 10px;
  animation: ${fadeInUp} 0.5s ease-out, ${pulse} 2s infinite ease-in-out;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-left: 3px solid #4ECDC4;
  overflow: hidden;
  position: relative;
  transform-origin: top center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    pointer-events: none;
    opacity: 0.3;
  }
`;

const LevelName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #4ECDC4;
  margin-bottom: 5px;
  animation: ${fadeInUp} 0.6s ease-out;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    height: 2px;
    background-color: #4ECDC4;
    animation: ${expandRight} 0.8s cubic-bezier(0.11, 0.94, 0.38, 0.98) forwards;
  }
`;

// 数字变化动画
const numberChange = keyframes`
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const LevelGoal = styled.div`
  font-size: 12px;
  color: #333;
  animation: ${fadeInUp} 0.7s ease-out;
  opacity: 0;
  animation-fill-mode: forwards;
  animation-delay: 0.2s;
  line-height: 1.5;
  
  strong {
    color: #FF6B6B;
    display: inline-block;
    animation: ${numberChange} 1s ease-out forwards;
  }
`;

const DiamondIcon = styled.span`
  color: #1E90FF;
  font-size: 18px;
`;

const DiamondValue = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #1E90FF;
`;

// 控制按钮容器
const ControlsContainer = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: ${props => props.$isCollapsed ? '10px' : '15px'};
  padding-top: ${props => props.$isCollapsed ? '0' : '10px'};
  border-top: ${props => props.$isCollapsed ? 'none' : '1px solid #eee'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

// 通用按钮样式
const ButtonBase = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: none;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  width: 80px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

// 商店按钮
const ShopButton = styled(ButtonBase)<{ $isCollapsed?: boolean }>`
  background-color: #FFD700;
  color: #333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  min-width: ${props => props.$isCollapsed ? '80px' : '140px'} !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background-color: #FFDF33;
  }
`;

// 商店图标
const ShopIcon = styled.span`
  font-size: 16px;
`;

// 按钮切换动画
const switchAnimate = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(0.95); }
  100% { transform: scale(1); }
`;

// 光晕效果动画
const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(255, 255, 255, 0.3) inset; }
  50% { box-shadow: 0 0 15px rgba(255, 255, 255, 0.5) inset; }
  100% { box-shadow: 0 0 5px rgba(255, 255, 255, 0.3) inset; }
`;

// 游戏模式切换按钮 - 滑动开关样式
const ModeToggle = styled.div<{ $isEndless?: boolean }>`
  position: relative;
  width: 110px;
  height: 32px;
  border-radius: 16px;
  background: ${props => props.$isEndless 
    ? 'linear-gradient(135deg, #43c6d9, #3aafbd)' 
    : 'linear-gradient(135deg, #9d65c9, #6a5ecd)'};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  border: 1px solid ${props => props.$isEndless ? '#3aafbd' : '#6a5ecd'};
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  user-select: none;
  animation: ${switchAnimate} 0.3s ease-out;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.3),
      rgba(255, 255, 255, 0.1)
    );
    border-radius: 16px 16px 0 0;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 3px;
    left: 50%;
    transform: translateX(-50%);
    width: 18px;
    height: 2px;
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 3px;
    opacity: 0.8;
  }
  
  &:hover {
    box-shadow: 0 4px 12px ${props => props.$isEndless 
      ? 'rgba(58, 175, 189, 0.4)' 
      : 'rgba(106, 94, 205, 0.4)'};
    transform: translateY(-1px);
    filter: brightness(1.05);
    
    &::after {
      animation: ${glow} 1.5s infinite;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    filter: brightness(0.95);
  }
`;

// 模式标签容器
const ModeLabelContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

// 模式标签滑动动画
const slideInFromTop = keyframes`
  from { transform: translateY(-30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const slideInFromBottom = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// 模式标签
const ModeLabel = styled.div<{ $active?: boolean; $isEndless?: boolean }>`
  position: absolute;
  top: 0;
  left: 4px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: white;
  transform: translateY(${props => props.$active ? '0' : props.$isEndless ? '-30px' : '30px'});
  opacity: ${props => props.$active ? 1 : 0};
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), 
              opacity 0.2s ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  letter-spacing: 1px;
  gap: 4px;
  pointer-events: none;
  animation: ${props => props.$active 
    ? (props.$isEndless ? slideInFromTop : slideInFromBottom) 
    : 'none'} 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  white-space: nowrap;
`;

// 模式图标
const ModeIcon = styled.span`
  font-size: 14px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  // margin-right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// 声音开关
const SoundToggle = styled(ButtonBase)<{ $isCollapsed?: boolean }>`
  background-color: #f1f1f1;
  color: #333;
  border-bottom: 3px solid #ddd;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background-color: #e9e9e9;
    color: #333;
  }
  
  &:active {
    background-color: #e0e0e0;
  }
`;

const SoundIcon = styled.span<{ $soundOn?: boolean }>`
  margin-right: 5px;
  color: ${props => props.$soundOn ? '#4ECDC4' : '#999'};
  opacity: ${props => props.$soundOn ? 1 : 0.7};
  transition: all 0.2s;
  font-size: 16px;
`;

// 展开/收起按钮
const CollapseToggleButton = styled(ButtonBase)<{ $isCollapsed?: boolean }>`
  background-color: ${props => props.$isCollapsed ? '#f8f8f8' : '#f1f1f1'};
  color: ${props => props.$isCollapsed ? '#4ECDC4' : '#333'};
  border-bottom: 3px solid ${props => props.$isCollapsed ? '#e2e2e2' : '#ddd'};
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.$isCollapsed ? '0 2px 8px rgba(0, 0, 0, 0.08)' : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  
  &:hover {
    background-color: ${props => props.$isCollapsed ? '#f0f8f8' : '#e9e9e9'};
    color: ${props => props.$isCollapsed ? '#3aaaa4' : '#333'};
    transform: translateY(-2px);
  }
  
  &:active {
    background-color: ${props => props.$isCollapsed ? '#e8f4f4' : '#e0e0e0'};
    transform: translateY(0);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.$isCollapsed 
      ? 'linear-gradient(to right, #6a5ecd, #4ECDC4)'
      : 'linear-gradient(to right, #4ECDC4, #E6C200)'};
    transform: scaleX(${props => props.$isCollapsed ? 0.7 : 1});
    transition: transform 0.3s ease, background 0.3s ease;
  }
  
  // 添加收起状态特效
  ${props => props.$isCollapsed && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle at center,
        rgba(78, 205, 196, 0.05) 0%,
        rgba(78, 205, 196, 0) 70%
      );
      opacity: 0;
      animation: ${pulse} 2s infinite ease-in-out;
    }
  `}
`;

// 添加收起状态的特殊标记
const CollapsedStatusIndicator = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #4ECDC4;
  opacity: 0.8;
  box-shadow: 0 0 4px rgba(78, 205, 196, 0.5);
  animation: ${pulse} 2s infinite ease-in-out;
`;

// 修改 ToggleIcon 为相对定位以支持绝对定位的指示器
const ToggleIcon = styled.span<{ $isCollapsed?: boolean }>`
  transform: rotate(${props => props.$isCollapsed ? '180deg' : '0deg'});
  transition: transform 0.3s ease;
  display: inline-block;
  margin-right: 4px;
  color: ${props => props.$isCollapsed ? '#6a5ecd' : '#4ECDC4'};
  font-size: ${props => props.$isCollapsed ? '16px' : '14px'};
`;

// 关卡进度条
const LevelProgress = styled.div<{ $progress: number }>`
  height: 5px;
  background-color: #eee;
  border-radius: 100px;
  margin-top: 8px;
  overflow: hidden;
  position: relative;
`;

const LevelProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  border-radius: 100px;
  width: ${props => `${Math.max(0, props.$progress)}%`};
  background: linear-gradient(90deg, #4ECDC4, #1A535C);
  transition: width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

// 清除数据按钮样式
const ClearDataButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f56565;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 10px;
  width: 100%;
  
  &:hover {
    background-color: #e53e3e;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.5);
  }
`;

// 确认弹窗样式
const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ConfirmBox = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 300px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ConfirmTitle = styled.h3`
  margin-top: 0;
  color: #333;
  font-size: 1.2rem;
`;

const ConfirmText = styled.p`
  color: #666;
  margin-bottom: 20px;
`;

const ConfirmButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ConfirmButton = styled.button<{ $isDanger?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  background-color: ${props => props.$isDanger ? '#f56565' : '#e2e8f0'};
  color: ${props => props.$isDanger ? 'white' : '#4a5568'};
  
  &:hover {
    background-color: ${props => props.$isDanger ? '#e53e3e' : '#cbd5e0'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.$isDanger ? 'rgba(245, 101, 101, 0.5)' : 'rgba(160, 174, 192, 0.5)'};
  }
`;

interface GameStatusPanelProps {
  gameState: GameState;
  moves: number;
  diamonds?: number;
  onToggleSound: () => void;
  onToggleShop: () => void;
  onToggleGameMode: () => void;
  soundOn: boolean;
  onToggleGameRules: (isOpen: boolean) => void;
  onTogglePanelCollapse: (value: boolean) => void;
  onClearData?: () => void;
  onOpenAuthModal: () => void;
}

const GameStatusPanel: React.FC<GameStatusPanelProps> = ({
  gameState, 
  moves, 
  diamonds = 0,
  onToggleSound,
  onToggleShop,
  onToggleGameMode,
  soundOn,
  onToggleGameRules,
  onTogglePanelCollapse,
  onClearData,
  onOpenAuthModal
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const toggleCollapse = () => {
    const newIsCollapsed = !isCollapsed;
    setIsCollapsed(newIsCollapsed);
    onTogglePanelCollapse(newIsCollapsed);
  };
  
  // 处理数据清除确认
  const handleClearDataClick = () => {
    setShowConfirm(true);
  };
  
  // 确认清除数据
  const handleConfirmClear = () => {
    if (onClearData) {
      onClearData();
    }
    setShowConfirm(false);
  };
  
  // 取消清除数据
  const handleCancelClear = () => {
    setShowConfirm(false);
  };
  
  // 获取当前关卡信息
  const currentLevel = gameState.mode === GameMode.LEVEL ? 
    getLevelById(gameState.currentLevel || 1) : null;
  
  // 计算关卡进度
  let progressPercentage = 0;
  if (currentLevel) {
    if (currentLevel.goal.type === LevelGoalType.SCORE) {
      progressPercentage = Math.min(100, (gameState.currentScore / currentLevel.goal.targetValue) * 100);
    } else if (currentLevel.goal.type === LevelGoalType.MAX_NUMBER) {
      progressPercentage = Math.min(100, (gameState.highestNumber / currentLevel.goal.targetValue) * 100);
    }
  }

const isLevelInfoVisible = !isCollapsed && gameState.mode === GameMode.LEVEL && currentLevel !== null;
const isEndlessMode = gameState.mode === GameMode.ENDLESS;

// 定义高亮数字的函数
const highlightNumbers = (text: string) => {
  // 使用正则表达式匹配数字
  return text.split(/(\d+)/).map((part, index) => {
    // 如果是数字，用strong标签包裹
    if (/^\d+$/.test(part)) {
      return <strong key={index}>{part}</strong>;
    }
    return part;
  });
};
  
  return (
    <StatusPanelContainer $isCollapsed={isCollapsed}>
      {isCollapsed && <CollapsedStatusIndicator />}
      <StatusPanelHeader $isCollapsed={isCollapsed}>
        {!isCollapsed && <StyledGameRulesButton onToggleGameRules={onToggleGameRules} />}
        {!isCollapsed && <LanguageSwitcher /> }
        {!isCollapsed && (
          <ModeToggle onClick={onToggleGameMode} $isEndless={isEndlessMode} title={isEndlessMode ? t('game.levelMode') : t('game.endlessMode')}>
            <ModeLabelContainer>
              <ModeLabel $active={isEndlessMode} $isEndless={true}>
                <ModeIcon>🔄</ModeIcon>
                {t('game.endlessMode')}
              </ModeLabel>
              <ModeLabel $active={!isEndlessMode} $isEndless={false}>
                <ModeIcon>🌟</ModeIcon>
                {t('game.levelMode')}
              </ModeLabel>
            </ModeLabelContainer>
          </ModeToggle>
        )}
        {!isCollapsed && <AuthButton onOpenModal={onOpenAuthModal} />}
      </StatusPanelHeader>
      
      <StatusGrid $isCollapsed={isCollapsed}>
        <StatusItem $isVisible={!isCollapsed} $index={0}>
          <StatusLabel>{t('game.totalScore')}</StatusLabel>
          <StatusValue>{gameState.totalScore}</StatusValue>
        </StatusItem>
        <StatusItem $isVisible={true} $index={1}>
          <StatusLabel $isImportant={true}>{t('game.score')}</StatusLabel>
          <StatusValue $isImportant={true}>{gameState.currentScore}</StatusValue>
        </StatusItem>
        <StatusItem $isVisible={!isCollapsed} $index={2}>
          <StatusLabel>{t('game.highestNumber')}</StatusLabel>
          <StatusValue>{gameState.highestNumber || 0}</StatusValue>
        </StatusItem>
        <StatusItem $isVisible={true} $index={3}>
          <StatusLabel $isImportant={isCollapsed} style={{fontWeight: 'bold'}}>{t('game.moves')}</StatusLabel>
          <StatusValue $isImportant={isCollapsed} style={{fontWeight: 'bold'}}>{moves}</StatusValue>
        </StatusItem>
      </StatusGrid>
      
      <LevelInfoContainer $isVisible={isLevelInfoVisible}>
        {isLevelInfoVisible && currentLevel && (
          <LevelInfo>
            <LevelName>{getLevelName(currentLevel) || t('game.unknownLevel')}</LevelName>
            <LevelGoal>
              {highlightNumbers(getLevelDescription(currentLevel))}
            </LevelGoal>
            <LevelProgress $progress={progressPercentage}>
              <LevelProgressBar $progress={progressPercentage} />
            </LevelProgress>
          </LevelInfo>
        )}
      </LevelInfoContainer>
      
      <ControlsContainer $isCollapsed={isCollapsed}>
        {!isCollapsed && (
          <SoundToggle onClick={onToggleSound} $isCollapsed={isCollapsed}>
            <SoundIcon $soundOn={soundOn}>
              {soundOn ? '🔊' : '🔇'}
            </SoundIcon>
            <span>{soundOn ? t('game.soundOn') : t('game.soundOff')}</span>
          </SoundToggle>
        )}
        
        {!isCollapsed && (
          <ShopButton onClick={onToggleShop} $isCollapsed={isCollapsed}>
            <ShopIcon>🛒</ShopIcon>
            <span>{t('game.shop')}</span>
            <DiamondIcon>💎</DiamondIcon>
            <DiamondValue>{diamonds}</DiamondValue>
          </ShopButton>
        )}
        
        <CollapseToggleButton 
          onClick={toggleCollapse} 
          $isCollapsed={isCollapsed}
          title={isCollapsed ? t('game.expandPanel') : t('game.collapsePanel')}
          style={{ width: '100%' }}
        >
          <ToggleIcon $isCollapsed={isCollapsed}>
            {/* {isCollapsed ? '▼' : '▲'} */}
            ▲
          </ToggleIcon>
          <span>{isCollapsed ? t('game.expand') : t('game.collapse')}</span>
        </CollapseToggleButton>
        
        {/* 清除数据按钮，只在面板展开时显示 */}
        {/* {!isCollapsed && onClearData && (
          <ClearDataButton onClick={handleClearDataClick}>
            🗑️ {t('game.clearData')}
          </ClearDataButton>
        )} */}
      </ControlsContainer>
      
      {/* 确认清除数据弹窗 */}
      {showConfirm && (
        <ConfirmDialog>
          <ConfirmBox>
            <ConfirmTitle>{t('game.clearDataTitle')}</ConfirmTitle>
            <ConfirmText>{t('game.clearDataConfirm')}</ConfirmText>
            <ConfirmButtons>
              <ConfirmButton onClick={handleCancelClear}>
                {t('game.cancel')}
              </ConfirmButton>
              <ConfirmButton $isDanger onClick={handleConfirmClear}>
                {t('game.confirm')}
              </ConfirmButton>
            </ConfirmButtons>
          </ConfirmBox>
        </ConfirmDialog>
      )}
    </StatusPanelContainer>
  );
};

export default GameStatusPanel; 